import type { Ref } from '@wix/materializer'
import { createRef } from '@wix/materializer'
import type { GhostPropertiesItem } from '@wix/thunderbolt-becky-root'
import { pointers } from '@wix/thunderbolt-becky-types'
import { createRefInResolvedRefsNamespaces, isGhostPropertyItem } from '../../../byRefUtils'
import { createByRefsisConsumptionCompNode } from '../../../compNodeUtils'
import { Component } from '@wix/thunderbolt-becky-root'
import { getTemplateFromInflatedId } from '../../../../utils'
import { MOBILE_PROPERTY_SUFFIX, SITE_NAMESPACE, STRUCTURE_ASPECT } from '../../../constants'

export type isGhostCompToViewItem = { isGhostComp: boolean; inflatedStructure?: Component }

type MaybeGhostPropertyItem = GhostPropertiesItem | undefined
type IsGhostComponentDeps = {
	templateComp: Ref<Component>
	hostOverrideDesktopPropertyItem: Ref<MaybeGhostPropertyItem>
	hostOverrideMobilePropertyItem: Ref<MaybeGhostPropertyItem> | null
	propertyItem: Ref<MaybeGhostPropertyItem>
}

const {
	data: {
		DATA_MAPS: { PROPERTIES },
	},
	components: {
		PROPERTY_TYPES: { PROPERTY_QUERY },
	},
} = pointers

export const isGhostComponent = createByRefsisConsumptionCompNode<
	IsGhostComponentDeps,
	isGhostCompToViewItem | undefined
>({
	getDependencies: (templateCompId, { id }, _ancestorRefCompId, scopedCreateRef, isMobileView) => {
		const propertyQuery = `${id}-${PROPERTY_QUERY}`
		const mobilePropertyQuery = `${id}-${PROPERTY_QUERY}${MOBILE_PROPERTY_SUFFIX}`
		return {
			templateComp: scopedCreateRef<Component>(STRUCTURE_ASPECT, templateCompId),
			hostOverrideDesktopPropertyItem: createRef<MaybeGhostPropertyItem>([
				SITE_NAMESPACE,
				PROPERTIES,
				propertyQuery,
			]),
			hostOverrideMobilePropertyItem: isMobileView
				? createRef<MaybeGhostPropertyItem>([SITE_NAMESPACE, PROPERTIES, mobilePropertyQuery])
				: null,
			propertyItem: createRefInResolvedRefsNamespaces<MaybeGhostPropertyItem>(
				PROPERTIES,
				isMobileView ? mobilePropertyQuery : propertyQuery
			),
		}
	},
	toViewItem: (
		inflatedStructure,
		{ templateComp, hostOverrideDesktopPropertyItem, hostOverrideMobilePropertyItem, propertyItem }
	) => {
		if (!templateComp) {
			return
		}

		return isGhostPropertyItem(hostOverrideDesktopPropertyItem) ||
			isGhostPropertyItem(hostOverrideMobilePropertyItem || propertyItem)
			? {
					// inflatedStructure is needed in onInvalidation to be added to byRefModel for byRef Api.
					// It's added at this stage since structure node is dependent on isGhostComp and once it's computed, its structure is already overwritten.
					// In addition, we override the id with the original compId because that's what byRef Api expects.
					isGhostComp: true,
					inflatedStructure: {
						...inflatedStructure,
						id: getTemplateFromInflatedId(inflatedStructure.id) || inflatedStructure.id,
					},
			  }
			: { isGhostComp: false }
	},
})
